import React, { useRef } from 'react';

import 'wicket';
import 'wicket/wicket-gmap3';
import { App } from '@flipdish/api-client-typescript';
import {
  GoogleMap,
  Marker,
  withGoogleMap,
  WithGoogleMapProps,
  withScriptjs,
  WithScriptjsProps,
} from 'react-google-maps';
import { connect } from 'react-redux';
import {
  compose,
  defaultProps,
  setDisplayName,
  UnpackICEWP,
  withHandlers,
  withProps,
  withPropsOnChange,
} from 'recompose';

import { deliveryZonesConstants } from '../../constants/deliveryzones.constants';
import { GMAP_API_KEY } from '../../constants/map.constant';
import { createLoadingSelector } from '../../selectors/loading.selector';
import { createDeliveryZoneIdsSelector } from '../../selectors/storeDeliveryZone.selector';
import MapItem from './components/MapItem';
import { IDeliveryZone } from './types';

const useDefaultProps = defaultProps({
  googleMapURL: `https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing${
    GMAP_API_KEY ? '&key=' + GMAP_API_KEY : ''
  }`, // &language=ja&region=JP
  loadingElement: <div style={{ height: `100%` }} />,
  containerElement: <div style={{ height: `100%` /*minHeight: '400px'*/ }} />,
  mapElement: <div style={{ height: `100%` }} />,
  options: {
    minZoom: 6,
    fullscreenControl: false,
    streetViewControl: false,
    mapTypeControl: false,
    zoomControl: false,
  },
});

const useRefProp = withProps(() => ({ mapRef: React.createRef<GoogleMap>() }));

const handlers = () => {
  return {
    panToCenter: ({ mapRef, mapCenter }: Props) => () => {
      if (mapRef && mapRef.current) {
        mapRef.current.panTo(mapCenter);
      }
    },
  };
};
type MappedHandlers = UnpackICEWP<typeof useHandlers>;
const useHandlers = withHandlers(handlers);

type MappedState = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
const mapStateToPropsFactory = (initialState, ownProps) => {
  const loadingSelector = createLoadingSelector([deliveryZonesConstants.DELIVERY_ZONE_CREATE]);
  const zoneIdsSelector = createDeliveryZoneIdsSelector(ownProps.storeId);

  return (state: AppState, ownProps) => {
    const canEdit = state.permissions.some(
      (p) => p === App.AppResourceSetEnum.UpdateDeliveryZones.toString()
    );

    return {
      createEventFired: loadingSelector(state),
      zoneIds: zoneIdsSelector(state),
      canEdit,
    };
  };
};

export interface IMapProps {
  storeId: number;
  mapCenter: google.maps.LatLng | google.maps.LatLngLiteral;
  isSmallScreen: boolean;
  onChange(changes: IDeliveryZone);
  onSelect(deliveryZoneId: number);
}
type Props = IMapProps &
  UnpackICEWP<typeof useRefProp> &
  UnpackICEWP<typeof useDefaultProps> &
  WithScriptjsProps &
  WithGoogleMapProps;

const GMap = compose<Props & MappedHandlers & MappedState, IMapProps>(
  setDisplayName('GMap'),
  useDefaultProps,
  useHandlers,
  withScriptjs,
  withGoogleMap,
  connect(mapStateToPropsFactory),
  withPropsOnChange(['createEventFired'], ({ createEventFired, panToCenter }) => {
    if (createEventFired) {
      panToCenter();
    }
  })
)(({ storeId, mapCenter, zoneIds, onChange, onSelect, isSmallScreen, options, canEdit }) => {
  const mapRef = useRef<GoogleMap>(null);

  const getCenterFromMapRef = () => {
    const getCenter = mapRef?.current?.getCenter();
    const lat = getCenter?.lat();
    const lng = getCenter?.lng();
    return lat && lng ? { lat, lng } : mapCenter;
  };

  return (
    <GoogleMap
      ref={mapRef}
      defaultZoom={isSmallScreen ? 11 : 12}
      defaultCenter={mapCenter}
      center={getCenterFromMapRef()}
      options={canEdit ? options : { ...options, gestureHandling: 'none' }}
    >
      <Marker position={mapCenter} />

      {zoneIds.map((id: number, idx) => {
        return (
          <MapItem
            key={id}
            storeId={storeId}
            deliveryZoneId={id}
            index={idx}
            onSelect={onSelect}
            onChange={onChange}
            disabled={!canEdit}
          />
        );
      })}
    </GoogleMap>
  );
});

export default GMap;
